import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { httpClient } from '../../../common/httpclient';
import { standaloneFieldSx } from '../../../common/fieldStyles';
import { PLAN_SEGMENTS } from '../../../common/planSegments';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';

const initialValues = {
    legal_name: '',
    trade_name: '',
    cnpj: '',
    state_registration: '',
    segment: '',
    email: '',
    cell_phone: '',
    phone_number: '',
    website: '',
    facebook: '',
    instagram: '',
    zip_code: '',
    address: '',
    number_address: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    owner_name: '',
    password: ''
};

const validationSchema = Yup.object({
    legal_name: Yup.string().trim().required('Razão social / nome da empresa é obrigatório'),
    segment: Yup.string().required('Segmento é obrigatório'),
    email: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
    state: Yup.string().max(2, 'Informe a UF com 2 letras'),
    number_address: Yup.number().transform((v, o) => (o === '' ? null : v)).typeError('Informe um número').integer('Informe um número inteiro').min(0, 'Número inválido').nullable(),
    owner_name: Yup.string().trim().required('Nome do responsável é obrigatório'),
    password: Yup.string().min(6, 'A senha deve ter pelo menos 6 caracteres').required('Senha é obrigatória')
});

export default function TenantForm() {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [created, setCreated] = useState(null);

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const payload = {
                    ...values,
                    number_address: values.number_address === '' ? null : parseInt(values.number_address, 10),
                    state: values.state.toUpperCase()
                };
                const response = await httpClient.post('/admin/subscription/tenants', payload);
                setCreated(response.data);
            } catch (error) {
                console.error('Error creating tenant:', error);
                const data = error.response?.data;
                setMessage((typeof data === 'string' && data) || data?.message || 'Erro ao cadastrar tenant');
                setSeverity('error');
                setOpenSnackbar(true);
            } finally {
                setLoading(false);
            }
        }
    });

    const field = (name, label, extra = {}) => (
        <TextField
            fullWidth
            id={name}
            name={name}
            label={label}
            value={formik.values[name]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            sx={{ ...standaloneFieldSx, flex: '1 1 240px', minWidth: 0 }}
            {...extra}
        />
    );

    const row = (children) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, flexWrap: 'wrap' }}>{children}</Box>
    );

    const section = (title, children) => (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>{title}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{children}</Box>
        </Box>
    );

    const handleCopy = () => {
        if (created?.client_id && navigator.clipboard) {
            navigator.clipboard.writeText(String(created.client_id));
        }
    };

    return (
        <PageScaffold
            loading={false}
            title="Cadastrar Tenant"
            titlePageTarget="Novo"
            links={[{ title: 'Assinaturas', link: '/admin/subscriptions' }]}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            <Box sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <IconButton onClick={() => navigate('/admin/subscriptions')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Cadastrar Tenant</Typography>
                        <Typography variant="body2" color="text.secondary">
                            A conta é criada já ativa, sem e-mail de ativação, no plano gratuito de 14 dias.
                        </Typography>
                    </Box>
                </Stack>

                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        backgroundColor: theme.palette.background.paper
                    }}
                >
                    <form onSubmit={formik.handleSubmit}>
                        {section('Empresa', <>
                            {row(<>
                                {field('legal_name', 'Razão Social / Nome da Empresa', { required: true })}
                                {field('trade_name', 'Nome Fantasia')}
                            </>)}
                            {row(<>
                                {field('cnpj', 'CNPJ')}
                                {field('state_registration', 'Inscrição Estadual')}
                                {field('segment', 'Segmento', {
                                    select: true,
                                    required: true,
                                    children: PLAN_SEGMENTS.map((s) => (
                                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                    ))
                                })}
                            </>)}
                        </>)}

                        {section('Contato', <>
                            {row(<>
                                {field('email', 'E-mail', { type: 'email', required: true })}
                                {field('cell_phone', 'Celular')}
                                {field('phone_number', 'Telefone')}
                            </>)}
                            {row(<>
                                {field('website', 'Site')}
                                {field('facebook', 'Facebook')}
                                {field('instagram', 'Instagram')}
                            </>)}
                        </>)}

                        {section('Endereço', <>
                            {row(<>
                                {field('zip_code', 'CEP')}
                                {field('address', 'Endereço')}
                                {field('number_address', 'Número', { type: 'number', inputProps: { min: 0, step: 1 } })}
                            </>)}
                            {row(<>
                                {field('complement', 'Complemento')}
                                {field('neighborhood', 'Bairro')}
                                {field('city', 'Cidade')}
                                {field('state', 'UF', { inputProps: { maxLength: 2 } })}
                            </>)}
                        </>)}

                        {section('Responsável / Acesso', <>
                            {row(<>
                                {field('owner_name', 'Nome do Responsável (nome e sobrenome)', { required: true })}
                                {field('password', 'Senha', { type: 'password', required: true, autoComplete: 'new-password' })}
                            </>)}
                            <Typography variant="caption" color="text.secondary">
                                O usuário de acesso é o primeiro nome do responsável. O código do cliente será exibido após o cadastro.
                            </Typography>
                        </>)}

                        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ p: 3 }}>
                            <Button onClick={() => navigate('/admin/subscriptions')} disabled={loading}>
                                Cancelar
                            </Button>
                            <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading}>
                                {loading ? 'Cadastrando...' : 'Cadastrar'}
                            </Button>
                        </Stack>
                    </form>
                </Paper>
            </Box>

            <Dialog open={Boolean(created)} maxWidth="xs" fullWidth>
                <DialogTitle>Tenant cadastrado</DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Conta ativa no plano gratuito de 14 dias.
                    </Alert>
                    <Typography variant="body2" color="text.secondary">Código do cliente</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }} data-testid="tenant-client-id">
                            {created?.client_id}
                        </Typography>
                        <IconButton onClick={handleCopy} aria-label="Copiar código do cliente">
                            <ContentCopyIcon />
                        </IconButton>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Usuário: <strong>{created?.username}</strong>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => navigate('/admin/subscriptions')} variant="contained">
                        Concluir
                    </Button>
                </DialogActions>
            </Dialog>
        </PageScaffold>
    );
}
